import React from 'react';

// Right-side detail panel for a selected supervisor: the supervisees table
// (with canEdit toggle and Remove) + the assigned-countries list, plus the
// Add Supervisee / Add Country selects for currently-available choices.
//
// Extracted from SupervisorAssignments to keep the parent's orchestration
// (data loading, mutation handlers, pendingOps Set) separate from the
// presentation. All state stays in the parent — this panel is pure props.

export function SupervisorDetailPanel({
  selectedSupervisor,
  supervisees,
  availableSupervisees,
  supervisorCountries,
  availableCountries,
  allUsers,
  pendingOps,
  onAddSupervisee,
  onRemoveSupervisee,
  onToggleEditPermission,
  onAddCountry,
  onRemoveCountry,
}) {
  return (
    <div className="supervisor-details">
      <h3>{selectedSupervisor.name}</h3>

      {/* Supervisees section */}
      <div className="section">
        <h4>Supervisees</h4>
        {supervisees.length > 0 && (
          <table className="supervisees-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Can Edit</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {supervisees.map((supervisee) => {
                const key = `supervisee:${supervisee.id}`;
                const pending = pendingOps.has(key);
                return (
                  <tr key={supervisee.id}>
                    <td>
                      {supervisee.name}
                      {supervisee.teamLeadId === selectedSupervisor.id && (
                        <span className="badge team-lead-badge">Team Lead</span>
                      )}
                    </td>
                    <td>
                      <input
                        type="checkbox"
                        checked={supervisee.canEdit}
                        disabled={pending}
                        onChange={() =>
                          onToggleEditPermission(supervisee.id, supervisee.canEdit)
                        }
                      />
                    </td>
                    <td>
                      <button
                        className="btn-secondary"
                        disabled={pending}
                        onClick={() => onRemoveSupervisee(supervisee.id)}
                      >
                        Remove
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}

        {supervisees.length === 0 && (
          <p className="empty-state">No supervisees assigned yet. Use "Add Supervisee" below.</p>
        )}

        {availableSupervisees.length > 0 && (
          <div style={{ marginTop: '15px' }}>
            <label htmlFor="add-supervisee-select">Add Supervisee:</label>
            <select
              id="add-supervisee-select"
              onChange={(e) => {
                const user = allUsers.find((u) => u.id === e.target.value);
                if (user) {
                  onAddSupervisee(user);
                  e.target.value = '';
                }
              }}
              defaultValue=""
            >
              <option value="">Select a user...</option>
              {availableSupervisees.map((user) => (
                <option key={user.id} value={user.id}>
                  {user.name} ({user.email})
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      {/* Countries section */}
      <div className="section">
        <h4>Assigned Countries</h4>
        {supervisorCountries.length > 0 && (
          <div className="countries-list">
            {supervisorCountries.map((country) => {
              const key = `country:${country.id}`;
              const pending = pendingOps.has(key);
              return (
                <div key={country.id} className="country-item">
                  <span>{country.name}</span>
                  <button
                    className="btn-secondary"
                    disabled={pending}
                    onClick={() => onRemoveCountry(country.id)}
                  >
                    ✕
                  </button>
                </div>
              );
            })}
          </div>
        )}

        {supervisorCountries.length === 0 && (
          <p className="empty-state">No countries assigned yet.</p>
        )}

        {availableCountries.length > 0 && (
          <div style={{ marginTop: '15px' }}>
            <label htmlFor="add-country-select">Add Country:</label>
            <select
              id="add-country-select"
              onChange={(e) => {
                if (e.target.value) {
                  onAddCountry(e.target.value);
                  e.target.value = '';
                }
              }}
              defaultValue=""
            >
              <option value="">Select a country...</option>
              {availableCountries.map((country) => (
                <option key={country.id} value={country.id}>
                  {country.name}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>
    </div>
  );
}
